<template>
  <v-card id="notification-campaign-recipients">
    <v-card-title class="d-flex align-center flex-wrap">
      <v-btn
        icon
        small
        class="me-2"
        :to="{ name: 'push-notification-list' }"
      >
        <v-icon size="20">
          {{ icons.mdiArrowLeft }}
        </v-icon>
      </v-btn>
      <span class="text-h6 me-3">{{ campaign ? campaign.name : 'Рассылка' }}</span>
      <v-chip
        v-if="campaign"
        small
        :color="campaignStatusColor(campaign.status)"
        text-color="white"
      >
        {{ campaign.statusName }}
      </v-chip>
    </v-card-title>

    <v-card-subtitle
      v-if="campaign && campaign.note"
      class="pb-0"
    >
      {{ campaign.note }}
    </v-card-subtitle>

    <v-card-text
      v-if="campaign"
      class="pt-4 pb-0"
    >
      <div class="d-flex flex-wrap">
        <div
          v-for="total in totals"
          :key="total.text"
          class="me-6 mb-2"
        >
          <div class="text--secondary text-caption">
            {{ total.text }}
          </div>
          <div class="text-h6">
            {{ total.value }}
          </div>
        </div>
      </div>

      <v-simple-table
        v-if="channels.length"
        dense
      >
        <thead>
          <tr>
            <th class="text-left">
              Канал
            </th>
            <th class="text-right">
              Устройств
            </th>
            <th class="text-right">
              Отправлено
            </th>
            <th class="text-right">
              Ошибок
            </th>
            <th class="text-right">
              Ждут
            </th>
            <th class="text-right">
              Истёк срок
            </th>
            <th class="text-right">
              Отменено
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="channel in channels"
            :key="channel.channel"
          >
            <td>{{ channel.channelName }}</td>
            <td class="text-right">
              {{ channel.total }}
            </td>
            <td class="text-right">
              {{ channel.sent }}
            </td>
            <td
              class="text-right"
              :class="channel.failed ? 'error--text font-weight-medium' : ''"
            >
              {{ channel.failed }}
            </td>
            <td class="text-right">
              {{ channel.pending }}
            </td>
            <td class="text-right">
              {{ channel.expired }}
            </td>
            <td class="text-right">
              {{ channel.cancelled }}
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </v-card-text>

    <v-card-text class="d-flex align-center flex-wrap pb-0">
      <div class="d-flex align-center pb-5">
        <v-select
          v-model="filters.status"
          :items="statusItems"
          dense
          outlined
          hide-details
          label="Исход"
          class="notification-campaign-recipients-status"
        ></v-select>
      </div>
    </v-card-text>

    <v-data-table
      :headers="headers"
      :items="recipients"
      :server-items-length="totalCount"
      :loading="loading"
      :page.sync="filters.pageNumber"
      :items-per-page.sync="filters.pageSize"
      :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
      disable-sort
      class="text-no-wrap"
    >
      <template #[`item.status`]="{item}">
        <v-chip
          small
          :color="recipientStatusColor(item.status)"
          text-color="white"
        >
          {{ item.statusName }}
        </v-chip>
      </template>

      <template #[`item.failed`]="{item}">
        <span :class="item.failed ? 'error--text font-weight-medium' : ''">{{ item.failed }}</span>
      </template>

      <template #[`item.error`]="{item}">
        <div
          v-if="item.error"
          class="notification-campaign-recipients-error error--text"
        >
          {{ item.error }}
        </div>
        <span v-else>—</span>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { mdiArrowLeft } from '@mdi/js'
import { mapFormHandlers } from '@/helpers/forms'
import {
  campaignChannels,
  campaignStatusColor,
  recipientStatusColor,
  recipientStatusItems,
} from '@/helpers/notifications'

export default {
  name: 'NotificationCampaignRecipients',
  data: () => ({
    icons: { mdiArrowLeft },
    campaignStatusColor,
    recipientStatusColor,
    statusItems: recipientStatusItems,
  }),
  computed: {
    ...mapFormHandlers('notificationCampaign/recipients', [
      'filters.pageNumber',
      'filters.pageSize',
      'filters.status',
    ]),
    ...mapState('notificationCampaign/recipients', {
      campaign: 'campaign',
      recipients: 'list',
      totalCount: 'totalCount',
      loading: 'loading',
    }),
    channels() {
      return campaignChannels(this.campaign)
    },
    totals() {
      const stats = this.campaign.stats || {}

      return [
        { text: 'Получателей', value: stats.recipients },
        { text: 'Поставлено в очередь', value: stats.recipientsQueued },
        { text: 'Без устройства', value: stats.recipientsWithoutDevice },
        { text: 'Отказались от рассылок', value: stats.recipientsUnsubscribed },
      ]
    },
    headers() {
      return [
        { text: 'Телефон', value: 'phoneNumber' },
        { text: 'Причина', value: 'reason' },
        { text: 'Исход', value: 'status' },
        { text: 'Устройств', value: 'deviceCount', align: 'end' },
        { text: 'Отправлено', value: 'sent', align: 'end' },
        { text: 'Ошибок', value: 'failed', align: 'end' },
        { text: 'Ошибка', value: 'error' },
      ]
    },
  },
  async created() {
    document.title = 'Получатели рассылки'
    await this.open(this.$route.params.id)
  },
  methods: {
    ...mapActions('notificationCampaign/recipients', ['open']),
  },
}
</script>

<style lang="scss" scoped>
#notification-campaign-recipients {
  .notification-campaign-recipients-status {
    max-width: 16rem;
  }

  .notification-campaign-recipients-error {
    white-space: normal;
    min-width: 16rem;
  }
}
</style>
